import { useCallback } from 'react';

import BannerType from '../../../../../types/banner/BannerType';
import { DEFAULT_VIDEO_ASPECT_RATIO, MOBILE_SQUARE_SIZE } from '../constants';

const useBannerSliderSizing = ({
  uiStore,
  aspectRatio,
  maximumCrossAxisSize,
  elementWidth,
  countMainAxisSize,
  countMinCrossAxisSize,
}) => {
  const getCrossAxisSize = useCallback(
    (ifMobileSquareMode) => {
      if (ifMobileSquareMode) {
        const availableWidth =
          elementWidth > 0 ? elementWidth : MOBILE_SQUARE_SIZE;

        return Math.min(MOBILE_SQUARE_SIZE, availableWidth);
      }

      const parentHeight =
        elementWidth > 0
          ? countMainAxisSize(elementWidth, aspectRatio)
          : maximumCrossAxisSize;

      return countMinCrossAxisSize(maximumCrossAxisSize, parentHeight);
    },
    [
      aspectRatio,
      countMainAxisSize,
      countMinCrossAxisSize,
      elementWidth,
      maximumCrossAxisSize,
    ]
  );

  const getVideoAspectRatio = useCallback((banner) => {
    if (
      banner.type === BannerType.VIDEO &&
      banner.video_poster_width > 0 &&
      banner.video_poster_height > 0
    ) {
      return banner.video_poster_width / banner.video_poster_height;
    }

    return DEFAULT_VIDEO_ASPECT_RATIO;
  }, []);

  const isPortraitVideoSlide = useCallback(
    (banner) => {
      return (
        banner.type === BannerType.VIDEO &&
        (!!banner.video_file || !!banner.video_link) &&
        getVideoAspectRatio(banner) < 1
      );
    },
    [getVideoAspectRatio]
  );

  const hasMobileSquareMode = useCallback(
    (slides, { isVertical = false } = {}) => {
      const hasMobileImages = slides.some((banner) => !!banner.mobile_image);

      return (
        uiStore.isSmallest &&
        !isVertical &&
        (hasMobileImages ||
          slides.some((banner) => isPortraitVideoSlide(banner)))
      );
    },
    [isPortraitVideoSlide, uiStore.isSmallest]
  );

  const getImageAspectRatio = useCallback(
    (banner) => {
      if (
        uiStore.isSmallest &&
        banner.mobile_image &&
        banner.mobile_image_width > 0 &&
        banner.mobile_image_height > 0
      ) {
        return banner.mobile_image_width / banner.mobile_image_height;
      }

      if (banner.image_width > 0 && banner.image_height > 0) {
        return banner.image_width / banner.image_height;
      }

      return aspectRatio;
    },
    [aspectRatio, uiStore.isSmallest]
  );

  const getSlideAspectRatio = useCallback(
    (banner) => {
      if (
        banner.type === BannerType.VIDEO &&
        (!!banner.video_file || !!banner.video_link)
      ) {
        return getVideoAspectRatio(banner);
      }

      return getImageAspectRatio(banner);
    },
    [getImageAspectRatio, getVideoAspectRatio]
  );

  const getSlideWidth = useCallback(
    (banner, shouldUseMobileSquareMode) => {
      const squareSize = getCrossAxisSize(shouldUseMobileSquareMode);
      const calculatedWidth = shouldUseMobileSquareMode
        ? squareSize
        : Math.round(squareSize * getSlideAspectRatio(banner));
      const maxSlideWidth =
        uiStore.isSmallest && !shouldUseMobileSquareMode
          ? elementWidth > 0
            ? elementWidth
            : calculatedWidth
          : calculatedWidth;

      return Math.max(1, Math.min(maxSlideWidth, calculatedWidth));
    },
    [elementWidth, getCrossAxisSize, getSlideAspectRatio, uiStore.isSmallest]
  );

  return {
    hasMobileSquareMode,
    isPortraitVideoSlide,
    getSlideWidth,
  };
};

export default useBannerSliderSizing;
